import React from 'react';
import { SurveyItemRow } from '../../pages/price-surveys/types';
import { ProposedSellingCell } from './ProposedSellingCell';
import { useDivisionalManagerSurveyProgressContextOptional } from '../../context/DivisionalManagerSurveyProgressContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import {
    isBelowCostSurveySubmitMessage,
    isProposedSellingBelowCostExVat,
} from '../../utility/surveyUtils';

interface ProposedSellingCellWrapperProps {
    row: SurveyItemRow;
}

export function ProposedSellingCellWrapper({
    row,
}: ProposedSellingCellWrapperProps) {
    const context = useDivisionalManagerSurveyProgressContextOptional();
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();
    const vatPercentage = row.resolvedVatPercentage ?? fallbackVatPercentage;

    if (!context) {
        return <span className="text-gray-400">-</span>;
    }

    const {
        getItemState,
        markForPcr,
        markAsNA,
        setProposedPrice,
        setNaReason,
        clearItemState,
        submitErrorIds,
        getSubmitErrorMessage,
        isSaving,
        isSubmitting,
    } = context;

    const itemState = getItemState(row.id);
    const disabled = isSaving || isSubmitting;
    const submitErrorMessage = getSubmitErrorMessage(row.id);
    const belowCostSubmitMessage =
        isBelowCostSurveySubmitMessage(submitErrorMessage);
    const hasRedSubmitError =
        submitErrorIds.includes(row.id) && !belowCostSubmitMessage;

    const isDeclined = itemState?.isDeclinedForPriceChange ?? false;
    const proposedFromState = itemState?.proposedSellingPrice;

    let valueForBelowCostCheck: number | null = null;
    if (!isDeclined) {
        if (proposedFromState === undefined) {
            valueForBelowCostCheck = row.suggestedPrice ?? null;
        } else {
            valueForBelowCostCheck = proposedFromState;
        }
    }

    const belowCostWarningActive =
        !isDeclined &&
        (belowCostSubmitMessage ||
            isProposedSellingBelowCostExVat(
                row.currentCostPrice,
                valueForBelowCostCheck,
                vatPercentage,
            ));

    return (
        <ProposedSellingCell
            row={row}
            itemState={itemState}
            onMarkForPcr={markForPcr}
            onMarkAsNA={markAsNA}
            onSetProposedPrice={setProposedPrice}
            onSetNaReason={setNaReason}
            onClearState={clearItemState}
            disabled={disabled}
            hasRedSubmitError={hasRedSubmitError}
            submitErrorMessage={submitErrorMessage}
            belowCostWarningActive={belowCostWarningActive}
        />
    );
}
